import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PostgrestError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { ListDef } from './registry';

export type Row = Record<string, unknown> & { id: string };

type Result<T = unknown> = PromiseLike<{ data: T; error: PostgrestError | null }>;
type Filter = { eq: (column: string, value: string) => Result };
type Select = Result<Row[] | null> & {
  order: (column: string, options?: { ascending?: boolean }) => Select;
};

/**
 * supabase-js גוזר טיפוסים משם טבלה ליטרלי. המסך הזה גנרי בכוונה: שם הטבלה
 * מגיע מה registry בזמן ריצה, ולכן הגזירה לא אפשרית כאן. זו הנקודה היחידה
 * שבה מוותרים עליה, והיא מוגבלת לארבע הפעולות שבשימוש.
 *
 * מה שמגן בפועל: שם הטבלה מוגבל ל TableName (נבדק בקומפילציה), RLS מאשר
 * או דוחה כל פעולה, ואילוצי הסכימה תופסים ערך לא תקין. כל דחייה מתורגמת
 * לעברית ב errorMessage.
 */
type GenericTable = {
  select: (columns: string) => Select;
  insert: (values: Record<string, unknown>) => Result;
  update: (values: Record<string, unknown>) => Filter;
  delete: () => Filter;
};

const from = (table: string): GenericTable =>
  (supabase as unknown as { from: (t: string) => GenericTable }).from(table);

const key = (table: string) => ['settings-list', table] as const;

/** עמודת המפתח הראשי. ברירת המחדל `id`, ויש טבלה שננעלת על טקסט. */
export const pkOf = (def: ListDef) => def.pk ?? 'id';

/**
 * בוחרים עמודות במפורש, בלי select('*') (מסמך התכנון, סעיף 5.1).
 * dedupe: בטבלה שהמפתח שלה גם שדה לעריכה, הוא היה מופיע פעמיים.
 */
function columns(def: ListDef): string {
  const names = [pkOf(def), ...def.fields.map((field) => field.name)];
  if (def.sortable) names.push('sort_order');
  if (def.deactivatable) names.push('is_active');
  return [...new Set(names)].join(', ');
}

export function useListRows(def: ListDef) {
  return useQuery({
    queryKey: key(def.table),
    queryFn: async (): Promise<Row[]> => {
      const query = from(def.table).select(columns(def));
      const { data, error } = await (def.sortable
        ? query.order('sort_order', { ascending: true }).order(pkOf(def))
        : query.order('name', { ascending: true }));

      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useListMutations(def: ListDef) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: key(def.table) });

  const run = async (result: Result) => {
    const { error } = await result;
    if (error) throw error;
  };

  const create = useMutation({
    mutationFn: (values: Record<string, unknown>) => run(from(def.table).insert(values)),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({ id, values }: { id: string; values: Record<string, unknown> }) =>
      run(from(def.table).update(values).eq(pkOf(def), id)),
    onSuccess: invalidate,
  });

  /**
   * מחיקה של פריט שיש לו היסטוריה נעשית כהשבתה (PRD 2.3).
   * טבלה בלי is_active נמחקת באמת, ואם יש תלות בבסיס הנתונים
   * השגיאה 23503 מתורגמת ל"לא ניתן למחוק, יש פריטים שמקושרים לזה".
   */
  const remove = useMutation({
    mutationFn: (id: string) =>
      run(
        def.deactivatable
          ? from(def.table).update({ is_active: false }).eq(pkOf(def), id)
          : from(def.table).delete().eq(pkOf(def), id),
      ),
    onSuccess: invalidate,
  });

  const restore = useMutation({
    mutationFn: (id: string) =>
      run(from(def.table).update({ is_active: true }).eq(pkOf(def), id)),
    onSuccess: invalidate,
  });

  /** החלפת מקום בין שתי שורות, כדי לשנות סדר תצוגה. */
  const swapOrder = useMutation({
    mutationFn: async ({ a, b }: { a: Row; b: Row }) => {
      const aOrder = Number(a.sort_order ?? 0);
      const bOrder = Number(b.sort_order ?? 0);
      const pk = pkOf(def);
      await run(from(def.table).update({ sort_order: bOrder }).eq(pk, String(a[pk])));
      await run(from(def.table).update({ sort_order: aOrder }).eq(pk, String(b[pk])));
    },
    onSuccess: invalidate,
  });

  return { create, update, remove, restore, swapOrder };
}
